import React, { useState, useEffect, useCallback } from 'react';
import { stockAPI } from '../services/api';
import { 
  Package, Plus, Edit, Trash2, AlertTriangle, 
  ArrowUpCircle,
  Warehouse, Boxes, TrendingDown, ScanBarcode
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import BarcodeScanPanel from '../components/BarcodeScanPanel';

const Stock = () => {
  const [activeTab, setActiveTab] = useState('barcode');
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    code: '', name: '', category_id: '', unit: 'adet', 
    quantity: 0, min_quantity: 0, price: 0, supplier_id: '', 
    barcode: '', description: ''
  });

  const [movementData, setMovementData] = useState({
    material_id: '', warehouse_id: '', movement_type: 'in',
    quantity: 1, unit_price: 0, reference_no: '', description: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'materials') {
        const [materialsRes, categoriesRes, warehousesRes, summaryRes] = await Promise.all([
          stockAPI.getMaterials(),
          stockAPI.getCategories(),
          stockAPI.getWarehouses(),
          stockAPI.getSummary()
        ]);
        setMaterials(materialsRes.data);
        setCategories(categoriesRes.data);
        setWarehouses(warehousesRes.data);
        setSummary(summaryRes.data);
      } else if (activeTab === 'barcode' || activeTab === 'movements') {
        const [movementsRes, materialsRes, warehousesRes, summaryRes] = await Promise.all([
          stockAPI.getMovements(),
          stockAPI.getMaterials(),
          stockAPI.getWarehouses(),
          stockAPI.getSummary()
        ]);
        setMovements(movementsRes.data);
        setMaterials(materialsRes.data);
        setWarehouses(warehousesRes.data);
        setSummary(summaryRes.data);
      } else if (activeTab === 'warehouses') {
        const warehousesRes = await stockAPI.getWarehouses();
        setWarehouses(warehousesRes.data);
      } else if (activeTab === 'categories') {
        const categoriesRes = await stockAPI.getCategories();
        setCategories(categoriesRes.data);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await stockAPI.updateMaterial(editingItem.id, formData);
      } else {
        await stockAPI.createMaterial(formData);
      }
      setShowModal(false);
      setEditingItem(null);
      setFormData({ code: '', name: '', category_id: '', unit: 'adet', quantity: 0, min_quantity: 0, price: 0, supplier_id: '', barcode: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
    }
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    try {
      await stockAPI.createMovement(movementData);
      toast.success('Stok hareketi kaydedildi');
      setShowMovementModal(false);
      setMovementData({ material_id: '', warehouse_id: '', movement_type: 'in', quantity: 1, unit_price: 0, reference_no: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Hareket kaydedilemedi');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu malzemeyi silmek istediğinize emin misiniz?')) {
      try {
        await stockAPI.deleteMaterial(id);
        fetchData();
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const filteredMaterials = materials.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'barcode', label: 'Barkod Giriş/Çıkış', icon: ScanBarcode },
    { id: 'materials', label: 'Hammaddeler', icon: Package },
    { id: 'movements', label: 'Stok Hareketleri', icon: ArrowUpCircle },
    { id: 'warehouses', label: 'Depolar', icon: Warehouse },
    { id: 'categories', label: 'Kategoriler', icon: Boxes },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Stok Yönetimi</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('barcode')}
            data-help-target="stock-barcode-scan"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <ScanBarcode size={18} />
            Barkod Tara
          </button>
          <button
            onClick={() => setShowMovementModal(true)}
            data-help-target="stock-movement-create"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowUpCircle size={18} />
            Manuel Giriş/Çıkış
          </button>
          <button
            onClick={() => { setEditingItem(null); setShowModal(true); }}
            data-help-target="stock-material-create"
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus size={18} />
            Yeni Hammadde
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-full">
              <Package className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Toplam Malzeme</p>
              <p className="text-2xl font-bold">{summary.total_materials || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Kritik Stok</p>
              <p className="text-2xl font-bold">{summary.low_stock || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingDown className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Stok Değeri</p>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_value)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-full">
              <Warehouse className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Aktif Depo</p>
              <p className="text-2xl font-bold">{summary.total_warehouses || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-10">Yükleniyor...</div>
      ) : (
        <>
          {activeTab === 'barcode' && (
            <div className="space-y-4">
              <BarcodeScanPanel
                title="Depo Barkod Giriş / Çıkış"
                subtitle="USB barkod okuyucu ile tarayın. Malzeme barkodu veya stok kodu kabul edilir."
                mode="in"
                allowModeSwitch
                warehouses={warehouses}
                onLookup={async (code) => {
                  const response = await stockAPI.getMaterialByBarcode(code);
                  return response.data;
                }}
                onConfirm={async ({ barcode, movement_type, quantity, warehouse_id }) => {
                  const response = await stockAPI.barcodeScan({
                    barcode,
                    movement_type,
                    quantity,
                    warehouse_id,
                  });
                  fetchData();
                  return response.data;
                }}
              />
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Hammaddelerde <strong>Barkod</strong> alanı dolu olmalıdır. Boşsa malzeme kodu ile de tarama yapılır.
              </div>
            </div>
          )}

          {activeTab === 'materials' && (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Malzeme ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full max-w-md px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Kod</th>
                      <th className="px-4 py-3 text-left">Barkod</th>
                      <th className="px-4 py-3 text-left">Malzeme Adı</th>
                      <th className="px-4 py-3 text-left">Kategori</th>
                      <th className="px-4 py-3 text-right">Miktar</th>
                      <th className="px-4 py-3 text-right">Min. Miktar</th>
                      <th className="px-4 py-3 text-right">Birim Fiyat</th>
                      <th className="px-4 py-3 text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map((item, index) => (
                      <tr key={index} className={`border-t ${item.quantity <= item.min_quantity ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3">{item.code}</td>
                        <td className="px-4 py-3 font-mono text-sm">{item.barcode || '-'}</td>
                        <td className="px-4 py-3 font-medium">{item.name}</td>
                        <td className="px-4 py-3">{item.category_name || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={item.quantity <= item.min_quantity ? 'text-red-600 font-bold' : ''}>
                            {item.quantity} {item.unit}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{item.min_quantity} {item.unit}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(item.price)}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 mr-2">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredMaterials.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center py-8 text-gray-500">Malzeme bulunamadı</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'movements' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Tarih</th>
                    <th className="px-4 py-3 text-left">Malzeme</th>
                    <th className="px-4 py-3 text-left">Depo</th>
                    <th className="px-4 py-3 text-center">Tip</th>
                    <th className="px-4 py-3 text-right">Miktar</th>
                    <th className="px-4 py-3 text-right">Tutar</th>
                    <th className="px-4 py-3 text-left">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">{new Date(item.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">{item.material_name} ({item.material_code})</td>
                      <td className="px-4 py-3">{item.warehouse_name}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${item.movement_type === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.movement_type === 'in' ? 'Giriş' : 'Çıkış'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.total_price)}</td>
                      <td className="px-4 py-3">{item.description || '-'}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-gray-500">Stok hareketi bulunamadı</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'warehouses' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Kod</th>
                    <th className="px-4 py-3 text-left">Depo Adı</th>
                    <th className="px-4 py-3 text-left">Adres</th>
                    <th className="px-4 py-3 text-left">Yönetici</th>
                    <th className="px-4 py-3 text-center">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouses.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">{item.code}</td>
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3">{item.address || '-'}</td>
                      <td className="px-4 py-3">{item.manager_name || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {warehouses.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-gray-500">Depo bulunamadı</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Kategori Adı</th>
                    <th className="px-4 py-3 text-left">Açıklama</th>
                    <th className="px-4 py-3 text-left">Üst Kategori</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3">{item.description || '-'}</td>
                      <td className="px-4 py-3">{item.parent_id ? 'Alt Kategori' : 'Ana Kategori'}</td>
                    </tr>
                  ))}
                  {categories.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center py-8 text-gray-500">Kategori bulunamadı</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Hammadde Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">{editingItem ? 'Hammadde Düzenle' : 'Yeni Hammadde'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Kod *</label>
                  <input type="text" required value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ad *</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Kategori</label>
                  <select value={formData.category_id} onChange={e => setFormData({...formData, category_id: e.target.value})}
                    className="w-full border rounded px-3 py-2">
                    <option value="">Seçin</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Birim</label>
                  <select value={formData.unit} onChange={e => setFormData({...formData, unit: e.target.value})}
                    className="w-full border rounded px-3 py-2">
                    <option value="adet">Adet</option>
                    <option value="kg">Kg</option>
                    <option value="mt">Metre</option>
                    <option value="L">Litre</option>
                    <option value="paket">Paket</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Miktar</label>
                  <input type="number" value={formData.quantity} onChange={e => setFormData({...formData, quantity: parseFloat(e.target.value)})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min. Miktar</label>
                  <input type="number" value={formData.min_quantity} onChange={e => setFormData({...formData, min_quantity: parseFloat(e.target.value)})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Birim Fiyat</label>
                  <input type="number" value={formData.price} onChange={e => setFormData({...formData, price: parseFloat(e.target.value)})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Barkod</label>
                  <input type="text" value={formData.barcode} onChange={e => setFormData({...formData, barcode: e.target.value})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Açıklama</label>
                  <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full border rounded px-3 py-2" rows="2" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">İptal</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stok Hareketi Modal */}
      {showMovementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Stok Giriş / Çıkış</h2>
            <form onSubmit={handleMovementSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Malzeme *</label>
                  <select required value={movementData.material_id} onChange={e => setMovementData({...movementData, material_id: e.target.value})}
                    className="w-full border rounded px-3 py-2">
                    <option value="">Seçin</option>
                    {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.code})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Depo *</label>
                  <select required value={movementData.warehouse_id} onChange={e => setMovementData({...movementData, warehouse_id: e.target.value})}
                    className="w-full border rounded px-3 py-2">
                    <option value="">Seçin</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">İşlem Tipi *</label>
                  <select required value={movementData.movement_type} onChange={e => setMovementData({...movementData, movement_type: e.target.value})}
                    className="w-full border rounded px-3 py-2">
                    <option value="in">Stok Giriş</option>
                    <option value="out">Stok Çıkış</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Miktar *</label>
                  <input type="number" required min="1" value={movementData.quantity} onChange={e => setMovementData({...movementData, quantity: parseFloat(e.target.value)})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Birim Fiyat</label>
                  <input type="number" value={movementData.unit_price} onChange={e => setMovementData({...movementData, unit_price: parseFloat(e.target.value)})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Referans No</label>
                  <input type="text" value={movementData.reference_no} onChange={e => setMovementData({...movementData, reference_no: e.target.value})}
                    className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Açıklama</label>
                  <textarea value={movementData.description} onChange={e => setMovementData({...movementData, description: e.target.value})}
                    className="w-full border rounded px-3 py-2" rows="2" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowMovementModal(false)} className="px-4 py-2 border rounded">İptal</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;
